import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../elements/ui/card';
import { Wind } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface WindCardProps {
  windSpeed: string | null;
  windDirection: string | null;
  windDegree: number | null;
}

const WindCard: React.FC<WindCardProps> = ({ windSpeed, windDirection, windDegree }) => {
  const { t } = useTranslation();
  const hasData = windSpeed != null || windDirection != null;

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Wind className="h-4 w-4" />
          Wind
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className="flex items-center justify-center h-24 text-gray-400 text-sm">{t('f-no-data')}</div>
        ) : (
          <div className="flex items-center gap-6">
            {/* Speed */}
            <div className="flex-1 text-center">
              <p className="text-3xl font-bold text-gray-900">{windSpeed ?? '—'}</p>
              <p className="text-xs text-gray-500 mt-1">km/h</p>
            </div>

            {/* Compass */}
            <div className="flex-shrink-0">
              <div className="relative w-20 h-20">
                <svg viewBox="0 0 80 80" className="w-full h-full">
                  {/* Compass circle */}
                  <circle cx="40" cy="40" r="36" fill="none" stroke="#e5e7eb" strokeWidth="2" />
                  {/* Cardinal labels */}
                  <text x="40" y="12" textAnchor="middle" className="fill-gray-400 text-[9px] font-medium">
                    N
                  </text>
                  <text x="70" y="43" textAnchor="middle" className="fill-gray-400 text-[9px] font-medium">
                    E
                  </text>
                  <text x="40" y="74" textAnchor="middle" className="fill-gray-400 text-[9px] font-medium">
                    S
                  </text>
                  <text x="10" y="43" textAnchor="middle" className="fill-gray-400 text-[9px] font-medium">
                    W
                  </text>
                  {/* Arrow */}
                  {windDegree != null && (
                    <g transform={`rotate(${windDegree}, 40, 40)`}>
                      <polygon points="40,14 36,40 44,40" fill="#ef4444" />
                      <polygon points="40,66 36,40 44,40" fill="#d1d5db" />
                    </g>
                  )}
                  {/* Center dot */}
                  <circle cx="40" cy="40" r="3" fill="#374151" />
                </svg>
              </div>
            </div>

            {/* Direction */}
            <div className="flex-1 text-center">
              <p className="text-2xl font-bold text-gray-900">{windDirection ?? '—'}</p>
              <p className="text-xs text-gray-500 mt-1">Direction</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WindCard;
